import type { IObservableArray } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { useAccount } from '@feathr/report_components';

import { useLocationFilters } from '../Targetings.useLocationFilters';
import { removeOrArchiveTargetings } from '../Targetings.utils';
import { useGeoFilterSetOperations } from './Locations.useGeoFilterSetOperations';
import { useModals } from './Locations.useModals';
import { determineGeoFilterMode, EGeoFilterMode } from './Locations.utils';

/**
 * Props for the useLocations hook.
 */
export interface IUseLocationsProps {
  /** The campaign to manage location filters for */
  campaign: Campaign;
  /** Observable array of targetings for the campaign */
  targetings: IObservableArray<Targeting>;
}

/**
 * Return type for the useLocations hook.
 */
interface IUseLocationsReturn {
  /** Current geofilter mode (none, saved_set, or custom_set) */
  geoFilterMode: EGeoFilterMode;
  /** The default geofilter set ID from account settings */
  defaultGeoSetId: string | undefined;
  /** Whether the user has explicitly declined geofilters for this session */
  hasUserDeclinedGeofilters: boolean;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Filtered list of geo targetings */
  filteredTargetings: Targeting[];
  /** Number of remaining available locations */
  remainingLocations: number;
  /** Errors preventing adding a new filter */
  addFilterErrors: string[];
  /** Whether any targetings are in an invalid state */
  hasInvalidTargetings: boolean;
  /** Modal state handlers */
  modals: ReturnType<typeof useModals>;
  /** Handler to add a new filter */
  handleAddFilter: () => void;
  /** Handler to remove a filter */
  handleRemoveFilter: (targeting: Targeting) => void;
  /** Handler to change geofilter mode */
  handleGeoFilterModeChange: (newMode: EGeoFilterMode) => void;
  /** Handler to open save custom set modal */
  handleSaveCustomSet: () => void;
  /** Handler to submit save custom set */
  handleSaveCustomSetSubmit: (name: string) => Promise<void>;
}

/**
 * Custom hook that manages all state and logic for the Locations component.
 *
 * Handles:
 * - Geofilter mode state (none, saved_set, custom_set)
 * - Save operations and loading states
 * - Filter management (add, remove)
 * - Modal state coordination
 *
 * @param props - Configuration props
 * @returns Object containing state values and handlers
 *
 * @example
 * ```tsx
 * const {
 *   geoFilterMode,
 *   filteredTargetings,
 *   handleAddFilter,
 *   handleGeoFilterModeChange,
 * } = useLocations({ campaign, targetings });
 * ```
 */
export function useLocations({ campaign, targetings }: IUseLocationsProps): IUseLocationsReturn {
  const { t } = useTranslation();
  const { Targetings } = useStore();
  const account = useAccount();

  const defaultGeoSetId = account.getSetting('default_geo_set');
  const hasUserDeclinedGeofiltersRef = useRef(false);
  const hasInitializedDefaultRef = useRef(false);

  const [geoFilterMode, setGeoFilterMode] = useState<EGeoFilterMode>(() => {
    const existingMode = determineGeoFilterMode(targetings);
    const geoKinds = new Set(['geo', 'geo_set']);
    const hasNoGeoTargetings = targetings.filter((t) => geoKinds.has(t.get('kind'))).length === 0;
    const isNewCampaign = campaign.isEphemeral;
    if (
      existingMode === EGeoFilterMode.NONE &&
      defaultGeoSetId &&
      hasNoGeoTargetings &&
      isNewCampaign
    ) {
      hasInitializedDefaultRef.current = true;
      return EGeoFilterMode.SAVED_SET;
    }
    return existingMode;
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (hasInitializedDefaultRef.current || hasUserDeclinedGeofiltersRef.current) {
      return;
    }

    if (!defaultGeoSetId) {
      return;
    }

    const geoKinds = new Set(['geo', 'geo_set']);
    const hasNoGeoTargetings = targetings.filter((t) => geoKinds.has(t.get('kind'))).length === 0;
    const isNewCampaign = campaign.isEphemeral;

    if (geoFilterMode === EGeoFilterMode.NONE && hasNoGeoTargetings && isNewCampaign) {
      hasInitializedDefaultRef.current = true;
      setGeoFilterMode(EGeoFilterMode.SAVED_SET);
    }
  }, [defaultGeoSetId, geoFilterMode, targetings, campaign.isEphemeral]);

  // Track if geo targetings existed before to detect when they're cleared externally
  const hadGeoTargetingsRef = useRef(false);
  // Track intentional mode changes to avoid auto-switching to NONE
  const isIntentionalModeChangeRef = useRef(false);

  // Compute active geo targetings count for dependency tracking
  const geoKinds = new Set(['geo', 'geo_set']);
  const activeGeoTargetingsCount = targetings.filter(
    (t) => geoKinds.has(t.get('kind')) && !t.get('is_archived'),
  ).length;

  useEffect(() => {
    const hasGeoTargetings = activeGeoTargetingsCount > 0;

    // If we previously had geo targetings and now have none, and we're not in NONE mode,
    // switch to NONE mode (this handles external clearing like "Clear audience" button)
    // Skip this if we're in the middle of an intentional mode change
    if (
      hadGeoTargetingsRef.current &&
      !hasGeoTargetings &&
      geoFilterMode !== EGeoFilterMode.NONE &&
      !isIntentionalModeChangeRef.current
    ) {
      hasUserDeclinedGeofiltersRef.current = true;
      setGeoFilterMode(EGeoFilterMode.NONE);
    }

    // Reset the intentional mode change flag after the effect runs
    isIntentionalModeChangeRef.current = false;
    hadGeoTargetingsRef.current = hasGeoTargetings;
  }, [activeGeoTargetingsCount, geoFilterMode]);

  const {
    filteredTargetings,
    remainingLocations,
    handleAddFilter,
    handleRemoveFilter,
    hasInvalidTargetings,
    addFilterErrors,
  } = useLocationFilters({ campaign, targetings });

  const { saveCustomSet: saveCustomSetOps } = useGeoFilterSetOperations({
    campaign,
    targetings,
  });

  const modals = useModals();

  const handleSaveCustomSet = useCallback((): void => {
    modals.saveCustomSet.open();
  }, [modals.saveCustomSet]);

  const handleSaveCustomSetSubmit = useCallback(
    async (name: string): Promise<void> => {
      setIsSaving(true);

      try {
        await saveCustomSetOps(name);

        setGeoFilterMode(EGeoFilterMode.SAVED_SET);
        modals.saveCustomSet.close();
        toast(t('Custom set "{{name}}" saved successfully', { name }), {
          type: 'success',
        });
      } catch (error) {
        showError(error, t);
      } finally {
        setIsSaving(false);
      }
    },
    [saveCustomSetOps, modals.saveCustomSet, t],
  );

  const handleGeoFilterModeChange = useCallback(
    (newMode: EGeoFilterMode): void => {
      // Mark this as an intentional mode change to prevent the auto-NONE logic
      isIntentionalModeChangeRef.current = true;

      const geoKinds = new Set(['geo', 'geo_set']);
      const allGeoTargetings = targetings.filter(
        (t) => geoKinds.has(t.get('kind')) && !t.get('is_archived'),
      );

      removeOrArchiveTargetings({
        targetingsToRemove: allGeoTargetings,
        targetingsArray: targetings,
        Targetings,
      });

      if (newMode === EGeoFilterMode.NONE) {
        hasUserDeclinedGeofiltersRef.current = true;
      }

      setGeoFilterMode(newMode);
    },
    [targetings, Targetings],
  );

  return {
    geoFilterMode,
    defaultGeoSetId,
    hasUserDeclinedGeofilters: hasUserDeclinedGeofiltersRef.current,
    isSaving,
    filteredTargetings,
    remainingLocations,
    addFilterErrors,
    hasInvalidTargetings,
    modals,
    handleAddFilter,
    handleRemoveFilter,
    handleGeoFilterModeChange,
    handleSaveCustomSet,
    handleSaveCustomSetSubmit,
  };
}
